import CrudSection from "../components/CrudSection";
import ForecastView from "./ForecastView";
import { Badge } from "../components/Badge";
import { OriginCell } from "../components/ImportedFromTag";
import { fetchAttachmentUrl } from "../services/attachmentsService";
import type { CrudColumn, CrudField } from "../types";
import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import { trackDealLostReasonSelected } from "../../../analytics/crmAnalytics";
import DealBillingRecipients from "../components/DealBillingRecipients";
import { isPlatformFeatureEnabled } from "../../../config/platformFeatures";
import { useOwnerAgents } from "../components/OwnerSelect";
import {
  LOST_REASONS,
  STAGES,
  STAGE_BY_ID,
  addDeal,
  formatMoney,
  removeDeal,
  updateDeal,
  useDeals,
  type BillingFrequency,
  type Deal,
  type DealBilling,
  type DealClosing,
  type DealStage,
  type LostReason,
} from "../services/dealsService";


// ===========================================================================
// Negocios y pipeline — live pipeline board + forecast + lista de negocios reales
// + facturas.
// The forecast (Pronóstico) is the module's dashboard / default landing.
// ===========================================================================

// --- Pipeline (kanban) -----------------------------------------------------
// The same screen as Panel CRM → Pipeline. See PipelineSection.
export { default as PipelineSection } from "./PipelineSection";

// --- Negocios (lista) ------------------------------------------------------
// LIVE: los mismos negocios de `crm_deals` que Pipeline y Pronóstico. Cada fila
// dice cuánto ingresó (monto), cuánto costó, el margen y con qué razones
// sociales paga el cliente (Dumax deposita con varias).
export interface NegocioRow extends Record<string, unknown> {
  id: string;
  nombre: string;
  empresa: string;
  monto: number;
  costo: number | null;
  /** #1491: reembolsos de nómina cargados al negocio (se suman al costo). */
  reembolsos: number;
  horas: number | null;
  etapa: DealStage;
  cierre: string;
  /** Alta del negocio: fecha del filtro de periodo cuando no hay cierre. */
  creado: string;
  pagadores: string[];
  /** Las mismas razones sociales, una por renglón: es lo que edita el formulario. */
  pagadoresTexto: string;
  notas: string;
  /** Responsable del negocio: id de un vendedor del CRM ("" = sin responsable). */
  responsable: string;
  /** Sigue a una compañía de la plataforma: su estado mueve la etapa (#1069). */
  plataforma: boolean;
  /** De dónde vino el negocio (p. ej. «hubspot» si lo trajo la integración). */
  origen: string;
  /** Adjunto con la cotización del negocio, si se identificó. */
  cotizacionId: string | null;
  cotizacionNombre: string | null;
  /** «mensual» o «proyecto»: qué representa el monto. */
  tipoMonto: string | null;
  // Cobro: con qué frecuencia se factura, en qué día(s) de corte y cuánto. Los
  // destinatarios salen de las personas del cliente en el CRM (no se capturan aquí).
  cobroFrecuencia: BillingFrequency;
  cobroDias: string;
  cobroMonto: number | null;
  /** Desglose por cuenta de depósito, un renglón por cuenta: "BBVA: 7500". */
  cobroDesglose: string;
  cobroCorreos: string;
  /** Plazo de pago propio del negocio; null = el de Finanzas → Configuración. */
  cobroDiasPago: number | null;
}

/** «Ver» la cotización adjunta de un negocio: abre el PDF con un enlace firmado que caduca. */
export function QuoteLink({ id, name }: { id: string | null; name: string | null }) {
  if (!id) return <span className="opacity-50">—</span>;
  return (
    <button
      type="button"
      title={name ?? "Ver la cotización adjunta"}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          window.open(await fetchAttachmentUrl(id), "_blank", "noopener,noreferrer");
        } catch {
          console.warn("No se pudo abrir la cotización", id);
        }
      }}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-primary-500 hover:bg-primary-500/10"
    >
      <i className="ri-file-pdf-2-line" />
      Ver
    </button>
  );
}

const money = (n: number | null) => (n === null ? "—" : formatMoney(n));

export const toNegocioRow = (d: Deal): NegocioRow => ({
  id: d.id,
  nombre: d.title,
  empresa: d.company,
  monto: d.amount,
  costo: d.cost ?? null,
  reembolsos: d.reimbursedCost ?? 0,
  horas: d.hours ?? null,
  etapa: d.stage,
  cierre: d.closeDate,
  creado: d.createdAt,
  pagadores: d.payers ?? [],
  pagadoresTexto: (d.payers ?? []).join("\n"),
  notas: d.notes ?? "",
  responsable: d.ownerId || "",
  plataforma: Boolean(d.platformCompanyId),
  origen: d.source ?? "",
  cotizacionId: d.quoteAttachmentId ?? null,
  cotizacionNombre: d.quoteAttachmentName ?? null,
  tipoMonto: d.amountKind ?? null,
  cobroFrecuencia: d.billing?.frequency ?? "none",
  cobroDias: (d.billing?.days ?? []).join(", "),
  cobroMonto: d.billing?.amount ?? null,
  cobroDesglose: (d.billing?.breakdown ?? []).map((b) => `${b.label}: ${b.amount}`).join("\n"),
  cobroCorreos: (d.billing?.emails ?? []).join("\n"),
  cobroDiasPago: d.billing?.dueDays ?? null,
});

const splitPayers = (v: unknown) =>
  String(v ?? "")
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

const toNumberOrNull = (v: unknown) => (v === "" || v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));

/** Campos de cobro del formulario → cobro del negocio. `undefined` si el formulario no los trae. */
function billingPatch(values: Record<string, unknown>): DealBilling | undefined {
  if (values.cobroFrecuencia === undefined) return undefined;
  const frequency = (["monthly", "biweekly"].includes(String(values.cobroFrecuencia)) ? values.cobroFrecuencia : "none") as BillingFrequency;
  const dueDays = toNumberOrNull(values.cobroDiasPago);
  return {
    frequency,
    days: String(values.cobroDias ?? "")
      .split(/[^0-9]+/)
      .map(Number)
      .filter((d) => Number.isInteger(d) && d >= 1 && d <= 31),
    amount: toNumberOrNull(values.cobroMonto),
    breakdown: String(values.cobroDesglose ?? "")
      .split(/[\n;]+/)
      .map((line) => /^(.*?)[:=\s]\s*\$?\s*([\d,]+(?:\.\d+)?)\s*$/.exec(line.trim()))
      .filter((m): m is RegExpExecArray => Boolean(m && m[1].trim()))
      .map((m) => ({ label: m[1].trim(), amount: Number(m[2].replace(/,/g, "")) })),
    emails: String(values.cobroCorreos ?? "")
      .split(/[\s,;]+/)
      .filter(Boolean),
    // Vacío = el plazo de omisión de Finanzas → Configuración.
    dueDays,
  };
}

/** Valores del formulario (texto) → cambios del negocio. */
export function negocioPatch(values: Record<string, unknown>): Partial<Deal> {
  const stage = String(values.etapa || "nuevo") as DealStage;
  const billing = billingPatch(values);
  return {
    ...(billing ? { billing } : {}),
    // Solo si el formulario trae el campo: "" quita al responsable.
    ...(values.responsable !== undefined ? { ownerId: String(values.responsable || "") } : {}),
    title: String(values.nombre || "").trim(),
    company: String(values.empresa || "").trim(),
    amount: Number(values.monto) || 0,
    cost: toNumberOrNull(values.costo),
    hours: toNumberOrNull(values.horas),
    stage,
    closeDate: String(values.cierre || ""),
    payers: splitPayers(values.pagadoresTexto ?? values.pagadores),
    notes: String(values.notas ?? ""),
  };
}

// --- Perdido con motivo (#1524) ---------------------------------------------
// Mover un negocio a «Perdido» exige el motivo (el backend responde 422 sin
// él): se pregunta en un diálogo con el catálogo y una nota opcional.

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);

/** Pregunta el motivo de pérdida; null si se cancela. */
export async function askLostReason(isDarkMode: boolean, dealTitle?: string): Promise<Pick<DealClosing, "lostReason" | "lostReasonNote"> | null> {
  const options = LOST_REASONS.map((r) => `<option value="${r.id}">${escapeHtml(r.label)}</option>`).join("");
  const control = `width:100%;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;border:1px solid ${isDarkMode ? "#3a3a3a" : "#d1d5db"};background:${isDarkMode ? "#232323" : "#fff"};color:${isDarkMode ? "#fff" : "#111827"}`;
  const result = await Swal.fire<{ lostReason: LostReason; lostReasonNote: string }>({
    title: "¿Por qué se perdió?",
    html:
      `<p style="font-size:.875rem;margin-bottom:.75rem">${dealTitle ? escapeHtml(dealTitle) : "Este negocio"} pasa a <b>Perdido</b>. Elige el motivo; en 30 días se abrirá una tarea para volver a intentarlo.</p>` +
      `<label for="lost-reason" style="display:block;text-align:left;font-size:.75rem;margin-bottom:.25rem">Motivo</label>` +
      `<select id="lost-reason" style="${control};margin-bottom:.75rem"><option value="">Elige un motivo…</option>${options}</select>` +
      `<label for="lost-reason-note" style="display:block;text-align:left;font-size:.75rem;margin-bottom:.25rem">Nota (opcional)</label>` +
      `<textarea id="lost-reason-note" rows="3" maxlength="1000" placeholder="Qué pasó, qué pidió el cliente…" style="${control}"></textarea>`,
    showCancelButton: true,
    confirmButtonText: "Marcar perdido",
    cancelButtonText: "Cancelar",
    confirmButtonColor: brand.primary,
    background: isDarkMode ? "#1f1f1f" : undefined,
    color: isDarkMode ? "#fff" : undefined,
    focusConfirm: false,
    preConfirm: () => {
      const select = document.getElementById("lost-reason") as HTMLSelectElement | null;
      const note = document.getElementById("lost-reason-note") as HTMLTextAreaElement | null;
      const lostReason = (select?.value || "") as LostReason | "";
      if (!lostReason) {
        Swal.showValidationMessage("Elige el motivo");
        return undefined;
      }
      return { lostReason, lostReasonNote: (note?.value || "").trim() };
    },
  });
  if (!result.isConfirmed || !result.value) return null;
  trackDealLostReasonSelected(result.value.lostReason, Boolean(result.value.lostReasonNote));
  return { lostReason: result.value.lostReason, lostReasonNote: result.value.lostReasonNote || null };
}

/**
 * Guarda los cambios de un negocio editado en la lista. Si pasa a «Perdido»
 * (y no lo estaba ni tenía motivo), primero pregunta el motivo; al cancelar,
 * no se guarda nada.
 */
export async function saveNegocio(id: string, values: Partial<NegocioRow>, deals: Deal[], isDarkMode: boolean): Promise<void> {
  const patch = negocioPatch(values);
  const current = deals.find((d) => d.id === id);
  // Los correos ya no se capturan aquí: se conservan los que el negocio tuviera guardados.
  const previous = current?.billing?.emails;
  if (patch.billing && previous) patch.billing = { ...patch.billing, emails: previous };
  const becomesLost = patch.stage === "perdido" && current && current.stage !== "perdido" && !current.closing?.lostReason;
  if (becomesLost) {
    const closing = await askLostReason(isDarkMode, current.title);
    if (!closing) return;
    patch.closing = closing;
  }
  updateDeal(id, patch);
}

/** Píldora de la etapa con el color de esa etapa (el mismo del tablero y el Pronóstico). */
function StagePill({ stage }: { stage: DealStage }) {
  const meta = STAGE_BY_ID[stage];
  if (!meta) return <Badge value={stage} />;
  return (
    <span
      data-testid={`stage-pill-${stage}`}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ${meta.pill}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

// --- Filtros de la lista -----------------------------------------------------
// Etapa, periodo e ingreso. El periodo mira la fecha de cierre; un negocio sin
// cierre cuenta por su fecha de alta.
export type NegocioPeriod = "all" | "this_month" | "last_month" | "this_quarter" | "this_year" | "last_year" | "custom";

export interface NegocioFilters {
  etapa: DealStage | "all";
  periodo: NegocioPeriod;
  /** Solo con periodo «custom» (YYYY-MM-DD; vacío = sin límite). */
  desde: string;
  hasta: string;
  /** Texto de los campos; vacío = sin límite. */
  ingresoMin: string;
  ingresoMax: string;
}

export const NO_FILTERS: NegocioFilters = { etapa: "all", periodo: "all", desde: "", hasta: "", ingresoMin: "", ingresoMax: "" };

const PERIOD_OPTIONS: { value: NegocioPeriod; label: string }[] = [
  { value: "all", label: "Todo" },
  { value: "this_month", label: "Este mes" },
  { value: "last_month", label: "Mes pasado" },
  { value: "this_quarter", label: "Este trimestre" },
  { value: "this_year", label: "Este año" },
  { value: "last_year", label: "Año pasado" },
  { value: "custom", label: "Personalizado" },
];

const isoDay = (y: number, m: number, d: number) => {
  const dt = new Date(y, m, d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

/** Rango [desde, hasta] (inclusive, YYYY-MM-DD; "" = sin límite) de un periodo. */
export function periodRange(f: Pick<NegocioFilters, "periodo" | "desde" | "hasta">, today = new Date()): [string, string] {
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (f.periodo) {
    case "this_month":
      return [isoDay(y, m, 1), isoDay(y, m + 1, 0)];
    case "last_month":
      return [isoDay(y, m - 1, 1), isoDay(y, m, 0)];
    case "this_quarter": {
      const q = Math.floor(m / 3) * 3;
      return [isoDay(y, q, 1), isoDay(y, q + 3, 0)];
    }
    case "this_year":
      return [isoDay(y, 0, 1), isoDay(y, 11, 31)];
    case "last_year":
      return [isoDay(y - 1, 0, 1), isoDay(y - 1, 11, 31)];
    case "custom":
      return [f.desde, f.hasta];
    default:
      return ["", ""];
  }
}

export function filterNegocios(rows: NegocioRow[], f: NegocioFilters, today = new Date()): NegocioRow[] {
  const [from, to] = periodRange(f, today);
  const min = toNumberOrNull(f.ingresoMin);
  const max = toNumberOrNull(f.ingresoMax);
  return rows.filter((r) => {
    if (f.etapa !== "all" && r.etapa !== f.etapa) return false;
    if (min !== null && r.monto < min) return false;
    if (max !== null && r.monto > max) return false;
    if (from || to) {
      const fecha = (r.cierre || r.creado || "").slice(0, 10);
      if (!fecha) return false;
      if (from && fecha < from) return false;
      if (to && fecha > to) return false;
    }
    return true;
  });
}

function NegociosFilterBar({
  filters,
  onChange,
  shown,
  total,
  income,
}: {
  filters: NegocioFilters;
  onChange: (next: NegocioFilters) => void;
  shown: number;
  total: number;
  income: number;
}) {
  const { isDarkMode } = useTheme();
  const control = `rounded-lg border px-2 py-1.5 text-sm outline-none ${
    isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
  }`;
  const caption = `font-medium uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-600"}`;
  const set = (patch: Partial<NegocioFilters>) => onChange({ ...filters, ...patch });
  const active = JSON.stringify(filters) !== JSON.stringify(NO_FILTERS);
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2 text-xs">
        <span className={caption}>Etapa</span>
        <select value={filters.etapa} onChange={(e) => set({ etapa: e.target.value as NegocioFilters["etapa"] })} className={control}>
          <option value="all">Todas</option>
          {STAGES.map((st) => (
            <option key={st.id} value={st.id}>{st.label}</option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2 text-xs">
        <span className={caption}>Periodo</span>
        <select
          title="Por fecha de cierre; sin cierre, por fecha de alta"
          value={filters.periodo}
          onChange={(e) => set({ periodo: e.target.value as NegocioPeriod })}
          className={control}
        >
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>
      {filters.periodo === "custom" && (
        <div className="flex items-center gap-2 text-xs">
          <input type="date" aria-label="Desde" value={filters.desde} max={filters.hasta || undefined} onChange={(e) => set({ desde: e.target.value })} className={control} />
          <span className={caption}>a</span>
          <input type="date" aria-label="Hasta" value={filters.hasta} min={filters.desde || undefined} onChange={(e) => set({ hasta: e.target.value })} className={control} />
        </div>
      )}
      <div className="flex items-center gap-2 text-xs">
        <span className={caption}>Ingreso</span>
        <input type="number" min={0} aria-label="Ingreso mínimo" placeholder="Mín." value={filters.ingresoMin} onChange={(e) => set({ ingresoMin: e.target.value })} className={`${control} w-28`} />
        <span className={caption}>a</span>
        <input type="number" min={0} aria-label="Ingreso máximo" placeholder="Máx." value={filters.ingresoMax} onChange={(e) => set({ ingresoMax: e.target.value })} className={`${control} w-28`} />
      </div>
      {active && (
        <button onClick={() => onChange(NO_FILTERS)} className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-primary-500 hover:bg-primary-500/10">
          <i className="ri-close-line" />
          Limpiar filtros
        </button>
      )}
      <span data-testid="negocios-resumen" className={`ml-auto text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
        {shown === total ? `${total} negocios` : `${shown} de ${total} negocios`} · Ingreso {formatMoney(income)}
      </span>
    </div>
  );
}

const negocioColumns: CrudColumn<NegocioRow>[] = [
  { key: "nombre", label: "Negocio" },
  {
    key: "empresa",
    label: "Empresa",
    render: (r) =>
      r.plataforma ? (
        <span
          className="inline-flex items-center gap-1 whitespace-nowrap"
          title="Compañía de la plataforma: Facturable → Ganado, Baja temporal / No facturable → Congelado, Baja total → Terminado"
        >
          {r.empresa || "—"}
          <i className="ri-links-line text-primary-500" aria-label="Vinculada a la plataforma" />
        </span>
      ) : (
        r.empresa || "—"
      ),
  },
  { key: "origen", label: "Origen", render: (r) => <OriginCell source={r.origen} /> },
  {
    key: "monto",
    label: "Ingreso",
    align: "right",
    render: (r) => (
      <span title={r.tipoMonto === "mensual" ? "Mensualidad cotizada, sin IVA" : r.tipoMonto === "proyecto" ? "Precio del proyecto, sin IVA" : undefined}>
        {money(r.monto)}
        {r.tipoMonto === "mensual" && <span className="ml-1 text-[10px] opacity-70">/mes</span>}
      </span>
    ),
  },
  {
    key: "cotizacionId",
    label: "Cotización",
    align: "center",
    render: (r) => <QuoteLink id={r.cotizacionId} name={r.cotizacionNombre} />,
  },
  { key: "horas", label: "Horas", align: "right", render: (r) => (r.horas === null ? "—" : r.horas.toLocaleString("es-MX", { maximumFractionDigits: 1 })) },
  {
    // #1491: el costo capturado más los reembolsos de nómina cargados al negocio.
    key: "costo",
    label: "Costo",
    align: "right",
    render: (r) =>
      r.costo === null && !r.reembolsos ? (
        "—"
      ) : (
        <span title={r.reembolsos ? `Incluye reembolsos de nómina: ${money(r.reembolsos)}` : undefined}>
          {money((r.costo ?? 0) + r.reembolsos)}
          {r.reembolsos ? <span className="block text-[11px] opacity-70">+ reembolsos {money(r.reembolsos)}</span> : null}
        </span>
      ),
  },
  {
    key: "margen" as keyof NegocioRow & string,
    label: "Margen",
    align: "right",
    render: (r) => (r.costo === null && !r.reembolsos ? "—" : money(r.monto - (r.costo ?? 0) - r.reembolsos)),
  },
  { key: "etapa", label: "Etapa", align: "center", render: (r) => <StagePill stage={r.etapa} /> },
  { key: "cierre", label: "Cierre" },
  {
    key: "pagadores",
    label: "Razones sociales",
    render: (r) =>
      r.pagadores.length ? (
        <span title={r.pagadores.join("\n")} className="whitespace-nowrap">
          {r.pagadores.length === 1 ? r.pagadores[0] : `${r.pagadores[0]} +${r.pagadores.length - 1}`}
        </span>
      ) : (
        "—"
      ),
  },
];

/** El campo «Responsable»; sus opciones (los vendedores activos) se ponen al montar la lista. */
const RESPONSABLE_FIELD: CrudField = {
  key: "responsable",
  label: "Responsable",
  type: "select",
  defaultValue: "",
  options: [{ value: "", label: "Sin responsable" }],
};

const negocioFields: CrudField[] = [
  { key: "nombre", label: "Negocio", required: true, placeholder: "Desarrollo Dumax" },
  { key: "empresa", label: "Empresa", placeholder: "Dumax" },
  { key: "monto", label: "Ingreso (MXN)", type: "number", placeholder: "52000" },
  { key: "horas", label: "Horas de desarrollo", type: "number", placeholder: "Vacío si no se conoce" },
  { key: "costo", label: "Costo (MXN)", type: "number", placeholder: "Vacío si no se conoce" },
  {
    key: "etapa",
    label: "Etapa",
    type: "select",
    required: true,
    defaultValue: "nuevo",
    options: STAGES.map((st) => ({ value: st.id, label: st.label })),
  },
  { key: "cierre", label: "Cierre", type: "date" },
  RESPONSABLE_FIELD,
  { key: "pagadoresTexto", label: "Razones sociales que pagan (una por renglón)", type: "textarea" },
  { key: "notas", label: "Notas", type: "textarea" },
  // Cobro: con esto el negocio genera su factura en cada fecha de corte y la manda al cliente.
  {
    key: "cobroFrecuencia",
    label: "Cobro: frecuencia de facturación",
    type: "select",
    defaultValue: "none",
    options: [
      { value: "none", label: "Sin facturación automática" },
      { value: "monthly", label: "Mensual (un día de corte)" },
      { value: "biweekly", label: "Quincenal (dos días de corte)" },
    ],
  },
  { key: "cobroDias", label: "Cobro: día(s) de corte del mes (31 = último día; en fin de semana, el viernes)", placeholder: "15, 31" },
  { key: "cobroMonto", label: "Cobro: monto por periodo (MXN)", type: "number", placeholder: "51800" },
  { key: "cobroDesglose", label: "Cobro: desglose por cuenta de depósito (Cuenta: monto, una por renglón; su suma es el monto)", type: "textarea" },
  { key: "cobroDiasPago", label: "Cobro: días para pagar (vacío = el plazo de Finanzas → Configuración → General)", type: "number", placeholder: "Plazo general" },
  {
    key: "cobroDestinatarios",
    label: "Cobro: a quién se le manda la factura",
    type: "info",
    render: (row) => <DealBillingRecipients dealId={row?.id ? String(row.id) : null} />,
  },
];

// Facturación del negocio (cobro por periodo y destinatarios) y su cotización:
// fuera donde la plataforma apaga facturas/cotizaciones del CRM (WAYAK).
const BILLING_FIELD_KEYS = new Set(["cobroFrecuencia", "cobroDias", "cobroMonto", "cobroDesglose", "cobroDiasPago", "cobroDestinatarios"]);
const QUOTE_COLUMN_KEYS = new Set(["cotizacionId"]);
const CRM_BILLING_ON = isPlatformFeatureEnabled("crm-facturacion");

export function NegociosListSection() {
  const deals = useDeals();
  const { isDarkMode } = useTheme();
  const allRows = useMemo(() => deals.map(toNegocioRow), [deals]);
  const [filters, setFilters] = useState<NegocioFilters>(NO_FILTERS);
  const rows = useMemo(() => filterNegocios(allRows, filters), [allRows, filters]);
  const income = useMemo(() => rows.reduce((sum, r) => sum + r.monto, 0), [rows]);
  // Responsable: los vendedores activos como opciones, y su nombre en la tabla.
  const owners = useOwnerAgents();
  const fields = useMemo(
    () =>
      negocioFields.filter((f) => CRM_BILLING_ON || !BILLING_FIELD_KEYS.has(f.key)).map((f) =>
        f.key !== RESPONSABLE_FIELD.key
          ? f
          : { ...f, options: [{ value: "", label: "Sin responsable" }, ...owners.assignable.map((a) => ({ value: a.id, label: a.name }))] },
      ),
    [owners],
  );
  const columns = useMemo<CrudColumn<NegocioRow>[]>(() => {
    const base = negocioColumns.filter((c) => CRM_BILLING_ON || !QUOTE_COLUMN_KEYS.has(c.key));
    const at = base.findIndex((c) => c.key === "etapa");
    const responsable: CrudColumn<NegocioRow> = {
      key: "responsable",
      label: "Responsable",
      render: (r) => (r.responsable ? owners.nameOf(r.responsable) ?? "—" : "—"),
    };
    return [...base.slice(0, at), responsable, ...base.slice(at)];
  }, [owners]);
  const store = useMemo(
    () => ({
      rows,
      add: (values: Omit<NegocioRow, "id">) => {
        const patch = negocioPatch(values);
        addDeal({
          title: patch.title || "Sin título",
          company: patch.company || "",
          contact: "",
          amount: patch.amount ?? 0,
          cost: patch.cost ?? null,
          hours: patch.hours ?? null,
          stage: patch.stage || "nuevo",
          ownerId: patch.ownerId || "",
          closeDate: patch.closeDate || "",
          priority: "medium",
          source: "Manual",
          notes: patch.notes,
          payers: patch.payers ?? [],
          billing: patch.billing,
        });
      },
      update: (id: string, values: Partial<NegocioRow>) => {
        void saveNegocio(id, values, deals, isDarkMode);
      },
      remove: (id: string) => removeDeal(id),
    }),
    [rows, deals, isDarkMode],
  );
  return (
    <CrudSection<NegocioRow>
      title="Negocios"
      subtitle="Negocios del CRM: ingreso, costo y margen"
      icon="ri-briefcase-line"
      columns={columns}
      fields={fields}
      seed={[]}
      store={store}
      searchKey="nombre"
      toolbar={<NegociosFilterBar filters={filters} onChange={setFilters} shown={rows.length} total={allRows.length} income={income} />}
    />
  );
}

// --- Pronóstico ------------------------------------------------------------
// LIVE weighted forecast (vs. quota, by owner/stage/period, CSV export).
// Shared with the Panel CRM. See ForecastView.
export const PronosticoSection = () => <ForecastView />;

// --- Cotizaciones ----------------------------------------------------------
// Mock CRUD (demo data, in-session). Lives under Negocios.
interface Cotizacion extends Record<string, unknown> {
  id: string; numero: string; cliente: string; total: string; estado: string;
}
const cotizacionColumns: CrudColumn<Cotizacion>[] = [
  { key: "numero", label: "No." },
  { key: "cliente", label: "Cliente" },
  { key: "total", label: "Total", align: "right" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <Badge value={r.estado} /> },
];
const cotizacionFields: CrudField[] = [
  { key: "numero", label: "Número", required: true, placeholder: "COT-0001" },
  { key: "cliente", label: "Cliente", placeholder: "Acme Logistics" },
  { key: "total", label: "Total", placeholder: "$24,000" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Borrador", options: [
    { value: "Borrador", label: "Borrador" }, { value: "Enviada", label: "Enviada" },
    { value: "Aceptada", label: "Aceptada" }, { value: "Rechazada", label: "Rechazada" }, { value: "Expirada", label: "Expirada" },
  ] },
];
const cotizacionSeed: Cotizacion[] = [
  { id: "ct1", numero: "COT-0001", cliente: "Acme Logistics", total: "$24,000", estado: "Enviada" },
  { id: "ct2", numero: "COT-0002", cliente: "Northstar Real Estate", total: "$12,500", estado: "Aceptada" },
  { id: "ct3", numero: "COT-0003", cliente: "Nova Marketing Group", total: "$36,000", estado: "Borrador" },
];
export const CotizacionesSection = () => (
  <CrudSection<Cotizacion> title="Cotizaciones" subtitle="Propuestas comerciales"
    icon="ri-file-list-2-line" columns={cotizacionColumns} fields={cotizacionFields} seed={cotizacionSeed} searchKey="cliente" />
);

// --- Facturas --------------------------------------------------------------
// LIVE (`crm_invoices`): facturas por periodo, envío al cliente y confirmación
// de pago. See FacturasSection.
export { default as FacturasSection } from "./FacturasSection";
